import {createAsyncThunk} from '@reduxjs/toolkit';
import Api, {ApiMethods} from '../../../middleware/api';
import {notify} from '../../notifier/services/actions';
import {MarkTemplateDto} from '../../../dto/MarkTemplateDto';
import {GlobalCriteriaDto} from '../../../dto/GlobalCriteriaDto';
import {MarkCriteriaDto} from '../../../dto/MarkCriteriaDto';
import {ProjectDto} from '../../../dto/ProjectDto';
import {PeriodDto} from '../../../dto/PeriodDto';
import {ProfileDto} from '../../../dto/ProfileDto';
import {SettingsRelationDto} from '../../../dto/RelationDto';

export const getManagers = createAsyncThunk(
    'settings/getManagers',
    async (projectId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `profiles/${projectId}/all-managers`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as ProfileDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const updateManagers = createAsyncThunk<ProfileDto[], {projectId: number; managers: ProfileDto[]}>(
    'settings/updateManagers',
    async ({projectId, managers}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `profiles/${projectId}/managers`,
            method: ApiMethods.POST,
            body: managers
        });
        if (httpStatus === 200) {
            return managers;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getPositions = createAsyncThunk(
    'settings/getPositions',
    async (projectId: number, {dispatch, rejectWithValue, requestId}) => {
        const endpoint = projectId === 0 ? 'profiles/all-positions' : `profiles/${projectId}/positions`;
        const {httpStatus, responseData} = await Api.fetch({
            endpoint,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return (Array.isArray(responseData) ? responseData : responseData[projectId]) as string[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getRelations = createAsyncThunk(
    'settings/getRelations',
    async (projectId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `relation/all/project/${projectId}`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as SettingsRelationDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const createRelation = createAsyncThunk<
    SettingsRelationDto,
    {relation: Partial<SettingsRelationDto>; period: PeriodDto}
>('settings/createRelation', async ({relation, period}, {dispatch, rejectWithValue, requestId}) => {
    const {fromProfile, toProfile, type, projectId} = relation;
    const {httpStatus, responseData} = await Api.fetch({
        endpoint: `relation/settings/save/${period.id}`,
        method: ApiMethods.POST,
        body: {fromProfileId: fromProfile?.id, toProfileId: toProfile?.id, projectId, type, relationId: null}
    });
    if (httpStatus === 201) {
        dispatch(notify({message: 'Success', key: requestId, variant: 'success'}));
        return {...relation, relationId: responseData} as SettingsRelationDto;
    }
    dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
    return rejectWithValue(responseData);
});

export const deleteRelation = createAsyncThunk(
    'settings/deleteRelation',
    async (relationId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `relation/delete/${relationId}`,
            method: ApiMethods.DELETE
        });
        if (httpStatus === 200) {
            dispatch(notify({message: 'Success', key: requestId, variant: 'warning'}));
            return relationId;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getProfiles = createAsyncThunk(
    'settings/searchProfiles',
    async (projectId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `profiles/${projectId}/all-profiles`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as ProfileDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getAllGlobalCriteria = createAsyncThunk(
    'reasons/getAllGlobalCriteria',
    async (_, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: 'mark/global-criteria/',
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as GlobalCriteriaDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const addGlobalCriteria = createAsyncThunk<
    GlobalCriteriaDto,
    {criteria: Partial<GlobalCriteriaDto>; isAffect: boolean}
>('reasons/addGlobalCriteria', async ({criteria, isAffect}, {dispatch, rejectWithValue, requestId}) => {
    const {httpStatus, responseData} = await Api.fetch({
        endpoint: `mark/global-criteria/?isAffect=${isAffect}`,
        method: ApiMethods.POST,
        body: {...criteria, id: null}
    });
    if (httpStatus === 201) {
        dispatch(notify({message: 'Success', key: requestId, variant: 'success'}));
        return {...criteria, id: responseData} as GlobalCriteriaDto;
    }
    dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
    return rejectWithValue(responseData);
});

export const editGlobalCriteria = createAsyncThunk<GlobalCriteriaDto, {criteria: GlobalCriteriaDto; isAffect: boolean}>(
    'reasons/editGlobalCriteria',
    async ({criteria, isAffect}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `mark/global-criteria/?isAffect=${isAffect}`,
            method: ApiMethods.PUT,
            body: criteria
        });
        if (httpStatus === 200) {
            dispatch(notify({message: 'Success', key: requestId, variant: 'success'}));
            return criteria;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const deleteGlobalCriteria = createAsyncThunk<number, {id: number; isAffect: boolean}>(
    'reasons/deleteGlobalCriteria',
    async ({id, isAffect}, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `mark/global-criteria/${id}?isAffect=${isAffect}`,
            method: ApiMethods.DELETE
        });
        if (httpStatus === 204) {
            dispatch(notify({message: 'Success', key: requestId, variant: 'warning'}));
            return id;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const getMarkTemplatesByProjectId = createAsyncThunk(
    'reasons/getMarkTemplatesByProjectId',
    async (projectId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `mark/template/${projectId}`,
            method: ApiMethods.GET
        });
        if (httpStatus === 200) {
            return responseData as MarkTemplateDto[];
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const addMarkCriteria = createAsyncThunk(
    'reasons/addMarkCriteria',
    async (markCriteria: Partial<MarkCriteriaDto>, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: 'mark/criteria/',
            method: ApiMethods.POST,
            body: markCriteria
        });
        if (httpStatus === 201) {
            dispatch(getMarkTemplatesByProjectId(markCriteria.projectId as number));
            dispatch(notify({message: 'Success', key: requestId, variant: 'success'}));
            return markCriteria;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const editMarkCriteria = createAsyncThunk(
    'reasons/editMarkCriteria',
    async (markCriteria: MarkCriteriaDto, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: 'mark/criteria/',
            method: ApiMethods.PUT,
            body: markCriteria
        });
        if (httpStatus === 201) {
            dispatch(getMarkTemplatesByProjectId(markCriteria.projectId));
            dispatch(notify({message: 'Success', key: requestId, variant: 'success'}));
            return markCriteria;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);

export const deleteMarkCriteria = createAsyncThunk(
    'reasons/deleteMarkCriteria',
    async (markCriteriaId: number, {dispatch, rejectWithValue, requestId}) => {
        const {httpStatus, responseData} = await Api.fetch({
            endpoint: `mark/criteria/${markCriteriaId}`,
            method: ApiMethods.DELETE
        });
        if (httpStatus === 200) {
            dispatch(notify({message: 'Success', key: requestId, variant: 'warning'}));
            return markCriteriaId;
        }
        dispatch(notify({message: responseData.message, key: requestId, variant: 'error'}));
        return rejectWithValue(responseData);
    }
);
